import React, { useState, useEffect } from 'react';
import './allFinalPerformaInvoice.css';

// Show country code together with number, e.g. "+1-7985733676"
// If number already starts with "+" (e.g. phoneNumber "+1-7985733111"), show as-is to avoid double code
const formatPhoneWithCode = (code, number) => {
  if (!number) return 'N/A';
  if (String(number).startsWith('+')) return number;
  return code ? `${code}-${number}` : number;
};

const AllFinalPerformaInvoice = ({ onViewInvoice, onEditInvoice, onConvertToOrder, autoOpenId, onAutoOpenConsumed, onNavigateToInquiry, onNavigateToOrder }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // --- Assigned To (same UI as All Inquiries; save API pending) ---
  const [assignedAdminSelection, setAssignedAdminSelection] = useState('');
  useEffect(() => {
    if (selectedInvoice) {
      setAssignedAdminSelection(
        selectedInvoice.assignedOrderAdminId ? String(selectedInvoice.assignedOrderAdminId) : ''
      );
    }
  }, [selectedInvoice]);
  const handleAssignedAdminChange = (e) => {
    setAssignedAdminSelection(e.target.value);
    // TODO: wire up the save-assignment API call here once it's available.
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const itemsPerPage = 20;
  const [sortBy, setSortBy] = useState('date-desc');
  // Table header sorting: key = column, dir = 'asc' | 'desc' | null
  const [columnSort, setColumnSort] = useState({ key: null, dir: null });
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [imageUrl, setImageUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [carSpec, setCarSpec] = useState(null);

  const API_BASE_URL = 'https://car.amar.co.jp/api/authorization/api/proforma-invoice';
  const CAR_SPEC_API_URL = 'https://car.amar.co.jp/api/master/car-specification/get-model-specification';

  useEffect(() => {
    fetchInvoices(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (autoOpenId && invoices.length > 0 && !loading) {
      const invoice = invoices.find(
        inv => inv.invoiceNo === autoOpenId ||
          inv.invoiceNo === String(autoOpenId) ||
          inv.id === autoOpenId ||
          inv.id === Number(autoOpenId)
      );
      if (invoice) {
        handleViewInvoice(invoice);
        if (onAutoOpenConsumed) onAutoOpenConsumed();
      }
    }
  }, [autoOpenId, invoices, loading]);

  const fetchInvoices = async (page) => {
    setLoading(true);
    setError(null);
    try {
      const backendPage = page - 1;
      const adminAuthToken = localStorage.getItem('adminAuthToken');
      const response = await fetch(`${API_BASE_URL}/all?page=${backendPage}&size=${itemsPerPage}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${adminAuthToken}`,
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch invoices: ${response.status} ${response.statusText}`);
      const result = await response.json();

      const data = Array.isArray(result.content) ? result.content : Array.isArray(result.data) ? result.data : Array.isArray(result) ? result : [];
      setTotalPages(result.totalPages || 0);
      setTotalElements(result.totalElements ?? result.totalInvoices ?? 0);

      const transformedData = data.map(invoice => ({
        id: invoice.proformaInvoiceId,
        inquiryId: invoice.inquiryId || null,
        invoiceNo: invoice.proformaInvoiceNo,
        orderRefId: invoice.orderRefId || null,
        invoiceDate: formatDateForDisplay(invoice.proformaInvoiceDate),
        currentStatus: invoice.currentStatus || invoice.status || null,
        customerName: invoice.customerName,
        customerEmail: invoice.email,
        countryCode: invoice.guestCountryCode || null,
        customerPhone: invoice.phoneNumber,
        shipToCountry: invoice.countryName,
        shipToPort: invoice.portName,
        vehicleBrand: invoice.carBrandName,
        vehicleModel: invoice.carModelName,
        vehicleYear: invoice.carYear,
        vehicleColor: invoice.carColor,
        engineCC: invoice.engineCC,
        vehicleCost: invoice.carPrice,
        shippingCost: invoice.shippingCost,
        shippingType: invoice.shippingType || null,
        shippingOption: invoice.shippingOption,
        m3: invoice.m3,
        grandTotal: invoice.grandTotal,
        countryId: invoice.countryId,
        portId: invoice.portId,
        carModelId: invoice.carModelId,
        assignedOrderAdminId: invoice.assignedOrderAdminId || null,
        assignedOrderAdminName: invoice.assignedOrderAdminName || null,
        // Registered profile details (from user registration)
        registeredUserId: invoice.registeredUserId ?? null,
        registeredFirstName: invoice.registeredFirstName || '',
        registeredLastName: invoice.registeredLastName || '',
        registeredEmailId: invoice.registeredEmailId || null,
        registeredCountryCode: invoice.registeredCountryCode || null,
        registeredCountry: invoice.registeredCountry || null,
        registeredMobileNumber: invoice.registeredMobileNumber || null,
        registeredDateOfBirth: invoice.registeredDateOfBirth || null,
        registeredGender: invoice.registeredGender || null
      }));

      setInvoices(transformedData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split(/[-/]/);
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  const fetchCarImage = async (carModelId) => {
    setImageLoading(true);
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${carModelId}`,
        { method: 'GET', headers: { 'accept': 'application/json', 'Accept-Language': 'en' } }
      );
      if (!response.ok) throw new Error('Failed to fetch car image');
      const blob = await response.blob();
      setImageUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error('Error fetching image:', err);
      setImageUrl('');
    } finally {
      setImageLoading(false);
    }
  };

  const fetchCarSpecification = async (carModelId) => {
    try {
      const response = await fetch(`${CAR_SPEC_API_URL}/${carModelId}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'Accept-Language': 'en' }
      });
      if (!response.ok) throw new Error(`Failed to fetch car specification: ${response.status}`);
      const result = await response.json();
      return result.data;
    } catch (err) {
      console.error('Error fetching car specification:', err);
      return null;
    }
  };

  const handleViewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setIsModalOpen(true);
    setCarSpec(null);
    fetchCarImage(invoice.carModelId);
    const spec = await fetchCarSpecification(invoice.carModelId);
    if (spec) setCarSpec(spec);
  };

  const handleEditInvoice = (invoice) => {
    const mappedData = {
      isEditMode: true,
      proformaInvoiceNo: invoice.invoiceNo,
      inquiryRefNumber: invoice.inquiryId,
      firstName: invoice.customerName ? invoice.customerName.split(' ')[0] : '',
      lastName: invoice.customerName ? invoice.customerName.split(' ').slice(1).join(' ') : '',
      userEmailId: invoice.customerEmail,
      guestCountryCode: invoice.countryCode,
      userMobileNumber: invoice.customerPhone,
      countryName: invoice.shipToCountry,
      portName: invoice.shipToPort,
      countryId: invoice.countryId,
      portId: invoice.portId,
      carBrandName: invoice.vehicleBrand,
      carModelName: invoice.vehicleModel,
      carColor: invoice.vehicleColor,
      carEngineCC: invoice.engineCC,
      carPrice: invoice.vehicleCost,
      carModelId: invoice.carModelId,
      shippingOption: invoice.shippingOption,
      shippingType: invoice.shippingType,
      baseShippingCost: invoice.shippingCost,
      volumeCubicMeters: invoice.m3,
    };
    if (onEditInvoice) onEditInvoice(mappedData);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedInvoice(null);
    setImageUrl('');
    setCarSpec(null);
  };

  const handleConvertToOrder = async (invoice) => {
    const loadingMessage = document.createElement('div');
    loadingMessage.className = 'allFPI-loading-overlay';
    loadingMessage.innerHTML = `<div class="allFPI-loading-overlay-content"><div class="allFPI-spinner"></div><p>Fetching vehicle details...</p></div>`;
    document.body.appendChild(loadingMessage);

    try {
      const carSpecData = await fetchCarSpecification(invoice.carModelId);
      const inquiryData = {
        id: invoice.id,
        proformaInvoiceNo: invoice.invoiceNo,
        userId: invoice.customerId,
        firstName: invoice.customerName.split(' ')[0],
        lastName: invoice.customerName.split(' ').slice(1).join(' '),
        userEmailId: invoice.customerEmail,
        userMobileNumber: invoice.customerPhone,
        guestCountryCode: invoice.countryCode,
        shippingType: invoice.shippingType,
        carModelId: invoice.carModelId,
        carBrandName: invoice.vehicleBrand,
        carModelName: invoice.vehicleModel,
        carYear: invoice.vehicleYear,
        carColor: carSpecData?.carColor || invoice.vehicleColor,
        carEngineCC: invoice.engineCC,
        carPrice: invoice.vehicleCost,
        shippingOption: invoice.shippingOption,
        baseShippingCost: invoice.shippingCost,
        totalCost: invoice.grandTotal,
        countryId: invoice.countryId,
        countryName: invoice.shipToCountry,
        portId: invoice.portId,
        portName: invoice.shipToPort,
        m3: invoice.m3,
        carRefNumber: carSpecData?.carRefNumber || 'N/A',
        carRegistrationDate: carSpecData?.carRegistrationDate || 'N/A',
        carManufactureDate: carSpecData?.carManufactureDate || 'N/A',
        carModelCode: carSpecData?.carModelCode || 'N/A',
        carGrade: carSpecData?.carGrade || 'N/A',
        carChassisNumber: carSpecData?.carChassisNumber || 'N/A',
        carEngineCode: carSpecData?.carEngineCode || 'N/A',
        carDoor: carSpecData?.carDoors || 'N/A',
        carWeight: carSpecData?.carWeight || 'N/A',
        carLocation: carSpecData?.carLocation || 'N/A',
        carLength: carSpecData?.carLength || 'N/A',
        carWidth: carSpecData?.carWidth || 'N/A',
        carHeight: carSpecData?.carHeight || 'N/A',
        carCategoriesName: carSpecData?.categoriesName || 'N/A',
        carFuel: carSpecData?.carFuelType || 'N/A',
        carTransmission: carSpecData?.carTransmission || 'N/A',
        carSteering: carSpecData?.carSteering || 'N/A',
        carSeat: carSpecData?.carSeatValue || 'N/A',
        carDrive: carSpecData?.carDriveType || 'N/A',
        airConditioning: carSpecData?.airConditioning || 'N/A',
        importType: carSpecData?.importType || 'N/A',
        isRecycleAmountPaid: carSpecData?.isRecycleAmountPaid || false,
        ourPick: carSpecData?.ourPick || false,
        specialDeal: carSpecData?.specialDeal || false
      };
      document.body.removeChild(loadingMessage);
      if (onConvertToOrder) onConvertToOrder(inquiryData);
    } catch (err) {
      if (loadingMessage.parentNode) document.body.removeChild(loadingMessage);
      console.error('Error converting to order:', err);
      alert(`Failed to fetch vehicle details: ${err.message}`);
    }
  };

  const getInvoiceStatus = (invoice) => invoice.currentStatus || 'N/A';

  const getStatusClassName = (status) => {
    if (!status || status === 'N/A') return 'allFPI-unknown';
    return 'allFPI-' + status.toLowerCase().replace(/\s+/g, '-');
  };

  const filteredInvoices = invoices.filter(invoice => {
    const searchMatch =
      invoice.invoiceNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.customerEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.vehicleBrand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.vehicleModel?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.id?.toString().includes(searchQuery);

    let dateMatch = true;
    if (dateRange.start && dateRange.end) {
      const invoiceDate = new Date(invoice.invoiceDate);
      dateMatch = invoiceDate >= new Date(dateRange.start) && invoiceDate <= new Date(dateRange.end);
    }
    return searchMatch && dateMatch;
  });

  // Value used for each sortable column
  const getColumnSortValue = (invoice, key) => {
    switch (key) {
      case 'inquiryId': return invoice.inquiryId || '';
      case 'invoiceNo': return invoice.invoiceNo || '';
      case 'customerName': return invoice.customerName || '';
      case 'customerEmail': return invoice.customerEmail || '';
      case 'carModel': return `${invoice.vehicleBrand || ''} ${invoice.vehicleModel || ''}`.trim();
      case 'invoiceDate': return invoice.invoiceDate ? new Date(invoice.invoiceDate).getTime() : 0;
      case 'shippingOption': return invoice.shippingOption || '';
      case 'status': return getInvoiceStatus(invoice);
      case 'assignedTo': return invoice.assignedOrderAdminName || '';
      case 'grandTotal': return parseFloat(invoice.grandTotal || 0);
      default: return '';
    }
  };

  // Click cycle: asc -> desc -> off
  const handleColumnSort = (key) => {
    setColumnSort(prev => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  };

  const renderSortIcon = (key) => {
    if (columnSort.key !== key) return <span className="allFPI-sort-icon allFPI-sort-idle">⇅</span>;
    return <span className="allFPI-sort-icon">{columnSort.dir === 'asc' ? '▲' : '▼'}</span>;
  };

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    // Header sort takes priority over the "Sort By" dropdown
    if (columnSort.key && columnSort.dir) {
      const valA = getColumnSortValue(a, columnSort.key);
      const valB = getColumnSortValue(b, columnSort.key);
      const result = typeof valA === 'number' && typeof valB === 'number'
        ? valA - valB
        : String(valA).localeCompare(String(valB), undefined, { numeric: true, sensitivity: 'base' });
      return columnSort.dir === 'asc' ? result : -result;
    }
    switch (sortBy) {
      case 'date-desc': return new Date(b.invoiceDate) - new Date(a.invoiceDate);
      case 'date-asc': return new Date(a.invoiceDate) - new Date(b.invoiceDate);
      case 'amount-desc': return b.grandTotal - a.grandTotal;
      case 'amount-asc': return a.grandTotal - b.grandTotal;
      case 'customer': return a.customerName.localeCompare(b.customerName);
      default: return 0;
    }
  });

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalElements);

  if (loading) {
    return (
      <div className="allFPI-container">
        <div className="allFPI-loading-state">
          <div className="allFPI-loading-spinner-large">⏳</div>
          <p>Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="allFPI-container">
        <div className="allFPI-error-state">
          <p>Error: {error}</p>
          <button onClick={() => fetchInvoices(currentPage)} className="allFPI-btn-retry">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="allFPI-container">
      {/* Header */}
      <div className="allFPI-header">
        <div className="allFPI-header-title-section">
          <h2>All Final Performa Invoices ({totalElements})</h2>
          <p className="allFPI-header-subtitle">Manage and track all sent invoices</p>
        </div>
      </div>

      {/* Statistics */}
      <div className="allFPI-stats-grid">
        <div className="allFPI-stat-card allFPI-stat-total">
          <div className="allFPI-stat-icon">📊</div>
          <div className="allFPI-stat-content">
            <h3>{totalElements}</h3>
            <p>Total Invoices</p>
          </div>
        </div>
        <div className="allFPI-stat-card allFPI-stat-amount">
          <div className="allFPI-stat-icon">💰</div>
          <div className="allFPI-stat-content">
            <h3>${invoices.reduce((sum, inv) => sum + inv.grandTotal, 0).toLocaleString()}</h3>
            <p>Page Total Amount</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="allFPI-filters-section">
        <div className="allFPI-filters-row">
          <div className="allFPI-filter-group">
            <label>Sort By</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="allFPI-filter-select">
              <option value="date-desc">Date (Newest First)</option>
              <option value="date-asc">Date (Oldest First)</option>
              <option value="amount-desc">Amount (High to Low)</option>
              <option value="amount-asc">Amount (Low to High)</option>
              <option value="customer">Customer Name</option>
            </select>
          </div>
          <div className="allFPI-filter-group allFPI-date-range-group">
            <label>Date Range</label>
            <div className="allFPI-date-range-inputs">
              <input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} className="allFPI-date-input" />
              <span className="allFPI-date-separator">to</span>
              <input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} className="allFPI-date-input" />
            </div>
          </div>
          <div className="allFPI-filter-group allFPI-search-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by name, email, car, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="allFPI-search-input"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="allFPI-table-container">
        {sortedInvoices.length === 0 ? (
          <div className="allFPI-no-results">No invoices found matching your criteria.</div>
        ) : (
          <table className="allFPI-table">
            <thead>
              <tr>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('inquiryId')}>INQUIRY ID {renderSortIcon('inquiryId')}</th>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('invoiceNo')}>Proforma INVOICE NO {renderSortIcon('invoiceNo')}</th>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('customerName')}>Customer {renderSortIcon('customerName')}</th>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('customerEmail')}>Email {renderSortIcon('customerEmail')}</th>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('carModel')}>Car Model {renderSortIcon('carModel')}</th>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('invoiceDate')}>Date {renderSortIcon('invoiceDate')}</th>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('shippingOption')}>Shipping {renderSortIcon('shippingOption')}</th>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('status')}>Status {renderSortIcon('status')}</th>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('assignedTo')}>Assigned To {renderSortIcon('assignedTo')}</th>
                <th className="allFPI-sortable-th" onClick={() => handleColumnSort('grandTotal')}>Grand Total {renderSortIcon('grandTotal')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedInvoices.map(invoice => {
                const status = getInvoiceStatus(invoice);
                const statusClass = getStatusClassName(status);

                return (
                  <tr key={invoice.id} className="allFPI-row">
                    <td>{invoice.inquiryId || 'N/A'}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: '#dc2626' }}>{invoice.invoiceNo}</span>
                    </td>
                    <td>
                      <div className="allFPI-customer-cell">{invoice.customerName || 'N/A'}</div>
                    </td>
                    <td>{invoice.customerEmail || 'N/A'}</td>
                    <td>
                      <div className="allFPI-car-model-info">
                        <div className="allFPI-car-brand">{invoice.vehicleBrand || 'N/A'}</div>
                        <div className="allFPI-car-model">{invoice.vehicleModel || 'N/A'}</div>
                      </div>
                    </td>
                    <td>{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('en-GB') : 'N/A'}</td>
                    <td>
                      <span className="allFPI-type-badge allFPI-shipping">{invoice.shippingOption || 'N/A'}</span>
                    </td>
                    <td>
                      <span className={`allFPI-status-badge ${statusClass}`}>{status}</span>
                    </td>
                    <td>{invoice.assignedOrderAdminName || 'Not Assigned'}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: '#059669', fontSize: '1rem' }}>
                        ${parseFloat(invoice.grandTotal || 0).toLocaleString()}
                      </span>
                    </td>
                    <td>
                      <div className="allFPI-action-buttons">
                        <button
                          className="allFPI-action-btn allFPI-view-btn"
                          onClick={() => handleViewInvoice(invoice)}
                          title="View Details"
                        >
                          View
                        </button>
                        <button
                          className="allFPI-action-btn allFPI-edit-btn"
                          onClick={() => handleEditInvoice(invoice)}
                          title={status === 'Order Confirmed' ? 'Cannot edit a confirmed order' : 'Edit Invoice'}
                          disabled={status === 'Order Confirmed'}
                        >
                          Edit
                        </button>
                        <button
                          className="allFPI-action-btn allFPI-convert-btn"
                          onClick={() => handleConvertToOrder(invoice)}
                          title={status === 'Order Confirmed' ? 'Already converted to order' : 'Convert To Order'}
                          disabled={status === 'Order Confirmed'}
                        >
                          {status === 'Order Confirmed' ? 'Converted' : 'Convert'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="allFPI-pagination-container" style={{ flexDirection: 'column', alignItems: 'center' }}>
          <div className="allFPI-pagination-buttons">
            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="allFPI-pagination-btn">
              ← Previous
            </button>
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              if (pageNumber === 1 || pageNumber === totalPages || (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)) {
                return (
                  <button key={pageNumber} onClick={() => paginate(pageNumber)} className={`allFPI-pagination-btn ${currentPage === pageNumber ? 'allFPI-active' : ''}`}>
                    {pageNumber}
                  </button>
                );
              } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                return <span key={pageNumber} className="allFPI-pagination-ellipsis">...</span>;
              }
              return null;
            })}
            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="allFPI-pagination-btn">
              Next →
            </button>
          </div>
          <div className="allFPI-pagination-info">
            Showing {indexOfFirstItem} to {indexOfLastItem} of {totalElements} invoices
          </div>
        </div>
      )}

      {/* View Modal */}
      {isModalOpen && selectedInvoice && (
        <div className="allFPI-modal-overlay">
          <div className="allFPI-modal">
            <div className="allFPI-modal-header">
              <h3>Invoice Details - {selectedInvoice.invoiceNo}</h3>
              <button className="allFPI-modal-close-x" onClick={closeModal}>×</button>
            </div>

            <div className="allFPI-modal-content">
              <div className="allFPI-car-image-section">
                {imageLoading ? (
                  <div className="allFPI-image-loading">Loading image...</div>
                ) : imageUrl ? (
                  <img src={imageUrl} alt={`${selectedInvoice.vehicleBrand} ${selectedInvoice.vehicleModel}`} className="allFPI-car-image" />
                ) : (
                  <div className="allFPI-car-image-placeholder">
                    <img src="/carousel/carslider.png" alt="Car placeholder" className="allFPI-placeholder-img" />
                  </div>
                )}
                <div className="allFPI-car-basic-info">
                  <h4>{selectedInvoice.vehicleBrand} {selectedInvoice.vehicleModel}</h4>
                  <div className="allFPI-type-display">
                    <span className="allFPI-type-badge allFPI-shipping">Proforma Invoice</span>
                  </div>
                  <div className="allFPI-car-meta">
                    <span>{carSpec?.carFuelType || 'N/A'}</span>
                    <span>{carSpec?.carTransmission || 'N/A'}</span>
                    <span>{carSpec?.carMileage || '0'} km</span>
                    <span>{selectedInvoice.vehicleColor || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="allFPI-details-sections">
                <div className="allFPI-details-section">
                  <h4 className="allFPI-section-title">Reference Numbers</h4>
                  <div className="allFPI-specs-grid">
                    <div className="allFPI-spec-item">
                      <span className="allFPI-spec-label">Inquiry Number</span>
                      {selectedInvoice.inquiryId ? (
                        <span className="allFPI-spec-value allFPI-clickable-ref" onClick={() => { closeModal(); onNavigateToInquiry && onNavigateToInquiry(selectedInvoice.inquiryId); }} title="View Inquiry">
                          {selectedInvoice.inquiryId}
                          <svg width="14" height="14" viewBox="0 0 14 14" style={{ marginLeft: '6px', verticalAlign: 'middle' }}>
                            <path d="M5.5 2L10 7L5.5 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      ) : <span className="allFPI-spec-value">N/A</span>}
                    </div>
                    <div className="allFPI-spec-item">
                      <span className="allFPI-spec-label">Proforma Invoice Number</span>
                      <span className="allFPI-spec-value">{selectedInvoice.invoiceNo || 'N/A'}</span>
                    </div>
                    <div className="allFPI-spec-item">
                      <span className="allFPI-spec-label">Order Invoice Number</span>
                      {selectedInvoice.orderRefId ? (
                        <span className="allFPI-spec-value allFPI-clickable-ref" onClick={() => { closeModal(); onNavigateToOrder && onNavigateToOrder(selectedInvoice.orderRefId); }} title="View Order">
                          {selectedInvoice.orderRefId}
                          <svg width="14" height="14" viewBox="0 0 14 14" style={{ marginLeft: '6px', verticalAlign: 'middle' }}>
                            <path d="M5.5 2L10 7L5.5 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      ) : <span className="allFPI-spec-value">N/A</span>}
                    </div>
                  </div>
                </div>

                {/* Customer Information — registered profile data */}
                <div className="allFPI-details-section">
                  <h4 className="allFPI-section-title">Customer Information</h4>
                  {selectedInvoice.registeredUserId ? (
                    <div className="allFPI-specs-grid">
                      <div className="allFPI-spec-item"><span className="allFPI-spec-label">Name</span><span className="allFPI-spec-value">{`${selectedInvoice.registeredFirstName} ${selectedInvoice.registeredLastName}`.trim() || 'N/A'}</span></div>
                      <div className="allFPI-spec-item"><span className="allFPI-spec-label">Email</span><span className="allFPI-spec-value">{selectedInvoice.registeredEmailId || 'N/A'}</span></div>
                      <div className="allFPI-spec-item"><span className="allFPI-spec-label">Phone</span><span className="allFPI-spec-value">{formatPhoneWithCode(selectedInvoice.registeredCountryCode, selectedInvoice.registeredMobileNumber)}</span></div>
                      <div className="allFPI-spec-item"><span className="allFPI-spec-label">Country</span><span className="allFPI-spec-value">{selectedInvoice.registeredCountry || 'N/A'}</span></div>
                      <div className="allFPI-spec-item"><span className="allFPI-spec-label">Gender</span><span className="allFPI-spec-value">{selectedInvoice.registeredGender || 'N/A'}</span></div>
                      <div className="allFPI-spec-item"><span className="allFPI-spec-label">Date of Birth</span><span className="allFPI-spec-value">{selectedInvoice.registeredDateOfBirth || 'N/A'}</span></div>
                      <div className="allFPI-spec-item"><span className="allFPI-spec-label">User Type</span><span className="allFPI-spec-value">Registered User</span></div>
                    </div>
                  ) : (
                    <div className="allFPI-specs-grid">
                      <div className="allFPI-spec-item"><span className="allFPI-spec-label">User Type</span><span className="allFPI-spec-value">Guest</span></div>
                      <div className="allFPI-spec-item"><span className="allFPI-spec-label">Registered Profile</span><span className="allFPI-spec-value">Not available (guest / no linked user)</span></div>
                    </div>
                  )}
                </div>

                {/* Inquiry Contact Details — data entered while making the inquiry */}
                <div className="allFPI-details-section">
                  <h4 className="allFPI-section-title">Inquiry Contact Details</h4>
                  <div className="allFPI-specs-grid">
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Name</span><span className="allFPI-spec-value">{selectedInvoice.customerName || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Email</span><span className="allFPI-spec-value">{selectedInvoice.customerEmail || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Phone</span><span className="allFPI-spec-value">{formatPhoneWithCode(selectedInvoice.countryCode, selectedInvoice.customerPhone)}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Country</span><span className="allFPI-spec-value">{selectedInvoice.shipToCountry || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Port</span><span className="allFPI-spec-value">{selectedInvoice.shipToPort || 'N/A'}</span></div>
                  </div>
                </div>

                <div className="allFPI-details-section">
                  <h4 className="allFPI-section-title">Vehicle Information</h4>
                  <div className="allFPI-specs-grid">
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Brand/Model</span><span className="allFPI-spec-value">{selectedInvoice.vehicleBrand} {selectedInvoice.vehicleModel}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Category</span><span className="allFPI-spec-value">{carSpec?.categoriesName || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Model Code</span><span className="allFPI-spec-value">{carSpec?.carModelCode || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Color</span><span className="allFPI-spec-value">{carSpec?.carColor || selectedInvoice.vehicleColor || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Registration Date</span><span className="allFPI-spec-value">{carSpec?.carRegistrationDate || 'N/A'}</span></div>
                  </div>
                </div>

                <div className="allFPI-details-section">
                  <h4 className="allFPI-section-title">Technical Specifications</h4>
                  <div className="allFPI-specs-grid">
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Reference Number</span><span className="allFPI-spec-value">{carSpec?.carRefNumber || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Chassis Number</span><span className="allFPI-spec-value">{carSpec?.carChassisNumber || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Engine CC</span><span className="allFPI-spec-value">{selectedInvoice.engineCC || carSpec?.carEngineCC || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Doors</span><span className="allFPI-spec-value">{carSpec?.carDoors || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Fuel Type</span><span className="allFPI-spec-value">{carSpec?.carFuelType || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Transmission</span><span className="allFPI-spec-value">{carSpec?.carTransmission || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Steering</span><span className="allFPI-spec-value">{carSpec?.carSteering || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Seats</span><span className="allFPI-spec-value">{carSpec?.carSeatValue || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Drive Type</span><span className="allFPI-spec-value">{carSpec?.carDriveType || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Location</span><span className="allFPI-spec-value">{carSpec?.carLocation || 'N/A'}</span></div>
                  </div>
                </div>

                <div className="allFPI-details-section">
                  <h4 className="allFPI-section-title">Invoice Details</h4>
                  <div className="allFPI-status-payment-container">
                    <div className="allFPI-status-display"><span className="allFPI-status-label">Invoice No:</span><span className="allFPI-status-value">{selectedInvoice.invoiceNo}</span></div>
                    <div className="allFPI-status-display"><span className="allFPI-status-label">Invoice Date:</span><span className="allFPI-status-value">{selectedInvoice.invoiceDate ? new Date(selectedInvoice.invoiceDate).toLocaleDateString('en-GB') : 'N/A'}</span></div>
                    <div className="allFPI-status-display"><span className="allFPI-status-label">Type:</span><span className="allFPI-status-value">Final Proforma Invoice</span></div>
                    <div className="allFPI-status-display">
                      <span className="allFPI-status-label">Assigned To:</span>
                      <select
                        value={assignedAdminSelection}
                        onChange={handleAssignedAdminChange}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#333',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                        }}
                      >
                        <option value="">Unassigned</option>
                        {selectedInvoice.assignedOrderAdminId && selectedInvoice.assignedOrderAdminName && (
                          <option value={String(selectedInvoice.assignedOrderAdminId)}>
                            {selectedInvoice.assignedOrderAdminName}
                          </option>
                        )}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="allFPI-details-section">
                  <h4 className="allFPI-section-title">Shipping Details</h4>
                  <div className="allFPI-specs-grid">
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Shipping Type</span><span className="allFPI-spec-value">{selectedInvoice.shippingType || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Shipping Option</span><span className="allFPI-spec-value">{selectedInvoice.shippingOption || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Shipping Cost</span><span className="allFPI-spec-value">${parseFloat(selectedInvoice.shippingCost || 0).toLocaleString()}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">M3 (Volume)</span><span className="allFPI-spec-value">{selectedInvoice.m3 ? `${selectedInvoice.m3} m³` : 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Destination Country</span><span className="allFPI-spec-value">{selectedInvoice.shipToCountry || 'N/A'}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Destination Port</span><span className="allFPI-spec-value">{selectedInvoice.shipToPort || 'N/A'}</span></div>
                  </div>
                </div>

                <div className="allFPI-details-section">
                  <h4 className="allFPI-section-title">Cost Summary</h4>
                  <div className="allFPI-specs-grid">
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Vehicle Cost</span><span className="allFPI-spec-value">${parseFloat(selectedInvoice.vehicleCost || 0).toLocaleString()}</span></div>
                    <div className="allFPI-spec-item"><span className="allFPI-spec-label">Shipping Cost</span><span className="allFPI-spec-value">${parseFloat(selectedInvoice.shippingCost || 0).toLocaleString()}</span></div>
                    <div className="allFPI-spec-item allFPI-total-cost"><span className="allFPI-spec-label">Grand Total</span><span className="allFPI-spec-value">${parseFloat(selectedInvoice.grandTotal || 0).toLocaleString()}</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="allFPI-modal-footer">
              <button className="allFPI-modal-close-btn" onClick={closeModal}>Close</button>
              <button
                className="allFPI-modal-edit-btn"
                onClick={() => { closeModal(); handleEditInvoice(selectedInvoice); }}
                disabled={getInvoiceStatus(selectedInvoice) === 'Order Confirmed'}
                title={getInvoiceStatus(selectedInvoice) === 'Order Confirmed' ? 'Cannot edit a confirmed order' : 'Edit Invoice'}
              >
                ✏️ Edit Invoice
              </button>
              <button
                className="allFPI-modal-convert-btn"
                onClick={() => { closeModal(); handleConvertToOrder(selectedInvoice); }}
                disabled={getInvoiceStatus(selectedInvoice) === 'Order Confirmed'}
              >
                {getInvoiceStatus(selectedInvoice) === 'Order Confirmed' ? '✅ Already Converted' : '🔄 Convert To Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllFinalPerformaInvoice;